// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import { put, select, takeLatest, takeEvery } from "redux-saga/effects";
import { Store } from "@reduxjs/toolkit";

import {
  ActionType,
  AuthFlow,
  raiseAction,
  IAction,
  IActionChangeInput,
  IActionChangeOutput,
  IActionAckAlarm,
  IActionTypeAuthFail,
  IActionTypeLogin,
} from "actions";
import { UserStateAWSError } from "types";
import { isSet } from "shared/utils/utils";
import { IState } from "states/states";

import { AlarmSource, AlarmState } from "shared/types/globtypes";
import { DataType, extractArrayData } from "shared/utils/apiutils";

export interface IDataLivePower {
  curTimeOfPeriod: string;
  accEnergy?: string; // acc. period energy, in real energy unit
  curEnergy?: string; // acc. period power, in power unit, not in energy unit
  curPower: string; // Momentanleistung
  curTargetValues: string;
  curRegulationPriorityStatus: string;
  curTrendLevel: string;
  energyMeterValues: string;
  inputStatus: string;
  outputStatus: string;
  outputStatusPhysical: string;
  conditionStatus: string;
  moduleStatus?: string; // provided since engine v7.6.306
}
export interface IDataLiveCharger {
  chargerCurP: string;
  chargerCurI: string;
  chargerCurU: string;
  chargerDevMaxI: string;
  chargerSetMaxI: string;
  chargerETotal: string;
  chargerESession: string;
  chargerState: string;
  chargerAuth: string;
}
export interface IDataLiveChargerPing {
  pingResult: string;
}
export type ILiveDataCloud = {
  siteId?: number;
  systemTime: number;
} & Partial<IDataLivePower> &
  Partial<IDataLiveCharger> &
  Partial<IDataLiveChargerPing>;

export function getAlarmState(isActive: boolean, isAcked: boolean) {
  if (isActive && !isAcked) return AlarmState.Active;
  if (!isActive && !isAcked) return AlarmState.AckPending;
  if (isActive && isAcked) return AlarmState.AckdActive;
  if (!isActive && isAcked) return AlarmState.Ackd;
  console.error("illegal alarm state");
  return AlarmState.Active;
}

// map of known AWS/Amplify exception names to user feedback messages
const AWS_AUTH_ERRORS: Record<string, UserStateAWSError> = {
  CodeMismatchException: UserStateAWSError.CODE_MISMATCH,
  ExpiredCodeException: UserStateAWSError.CODE_EXPIRED,
  InvalidPasswordException: UserStateAWSError.PASSWORD_INVALID,
  EmptySignInUsername: UserStateAWSError.USERNAME_MISSING,
  EmptySignUpUsername: UserStateAWSError.USERNAME_MISSING,
  EmptyConfirmSignUpUsername: UserStateAWSError.USERNAME_MISSING,
  EmptySignInPassword: UserStateAWSError.PASSWORD_MISSING,
  EmptyConfirmSignUpCode: UserStateAWSError.CODE_MISSING,
  EmptyConfirmResetPasswordConfirmationCode: UserStateAWSError.CODE_MISSING,
  EmptyChallengeResponse: UserStateAWSError.CODE_MISSING,
  EmptyVerifyTOTPSetupCode: UserStateAWSError.CODE_MISSING,
  // signals an invalid code during TOTP verification
  EnableSoftwareTokenMfaException: UserStateAWSError.CODE_MISMATCH,
};

// convert error object to string to have it readable in Redux action viewer
export function errorToString(error: unknown /* typically Response | Error */): string {
  if ((error as any).hasOwnProperty("errors")) {
    try {
      return ((error as any).errors as Error[])
        .map((curErr) => {
          return curErr.message;
        })
        .join(", ");
    } catch {
      // nothing to do here, try again with other content below
    }
  }
  // we assume this is an error from the http request?
  if (error instanceof Response && error.statusText) {
    // if we cannot get further details, we provide the http error as the only error
    return error.statusText;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return "Unknown error.";
}

// raise the consolidated auth failure action; maps known AWS/Amplify exception names to
// translated user feedback and passes unknown errors on as raw text
export function* raiseAuthError(
  flow: AuthFlow,
  error: unknown,
  overrides?: Record<string, UserStateAWSError>,
) {
  const errorName = (error as Error).name;
  const awsError = overrides?.[errorName] ?? AWS_AUTH_ERRORS[errorName];
  const payload: IActionTypeAuthFail =
    isSet(awsError) ? { flow, awsError } : { flow, errorText: errorToString(error) };
  yield put(raiseAction(ActionType.ACT_AUTH_FAIL, payload));
}

export abstract class ApiSagas {
  // main entry point to load sagas at startup
  public *initSagas() {
    yield takeLatest(ActionType[ActionType.ACT_LOGIN_REQ], this.doLogin.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_LOGOUT_REQ], this.doLogout.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_PARAMETER_REQ], this.getParameters.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_ALARMS_REQ], this.getAlarms.bind(this));
    yield takeEvery(ActionType[ActionType.ACT_ACK_ALARM_REQ], this.ackAlarm.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_ACK_ALL_ALARMS_REQ], this.ackAllAlarms.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_TRENDINFO_REQ], this.getTrendInfo.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_TREND_REQ], this.getTrend.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_GET_IOTREND_REQ], this.getIoTrend.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_CHANGE_INPUT_REQ], this.setInput.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_CHANGE_OUTPUT_REQ], this.setOutput.bind(this));
    yield takeLatest(ActionType[ActionType.ACT_SWITCH_SITE], this.switchSite.bind(this));
  }

  public abstract initApi(store?: Store): any;
  public abstract doAutoLogin(): any;

  protected abstract doLogin(action: IAction<IActionTypeLogin>): any;
  protected abstract doLogout(action: IAction<undefined>): any;
  protected abstract getParameters(action: IAction<undefined>): any;
  protected abstract getAlarms(action: IAction<undefined>): any;
  protected abstract getTrendInfo(action: IAction<undefined>): any;
  protected abstract getTrend(action: IAction<undefined>): any;
  protected abstract getIoTrend(action: IAction<undefined>): any;
  protected abstract setInput(action: IAction<IActionChangeInput>): any;
  protected abstract setOutput(action: IAction<IActionChangeOutput>): any;
  // sends the ack of an alarm, which also acks all older alarms of the same source; returns success
  protected abstract sendAlarmAck(ack: IActionAckAlarm): any;

  protected getState = (state: IState) => state;

  protected *ackAlarm(action: IAction<IActionAckAlarm>): any {
    try {
      if (!((yield* this.sendAlarmAck(action.payload)) as boolean)) {
        yield put(raiseAction(ActionType.ACT_ACK_ALARM_FAIL, `Alarm ack failed.`));
        return;
      }
      yield put(raiseAction(ActionType.ACT_ACK_ALARM_SUCC, action.payload));
      // load new state
      yield put(raiseAction(ActionType.ACT_GET_ALARMS_REQ));
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_ACK_ALARM_FAIL, error);
    }
  }

  // ack all alarms with one request per source, as an ack covers all older alarms of its source
  protected *ackAllAlarms(): any {
    const state = (yield select(this.getState)) as IState;
    const newestPerSource = new Map<AlarmSource, IActionAckAlarm>();
    for (const curAlarm of state.data.alarms) {
      if ([AlarmState.Ackd, AlarmState.AckdActive].includes(curAlarm.state)) continue;
      const known = newestPerSource.get(curAlarm.source);
      if (!known || curAlarm.alarmTimestamp > known.alarmTimestamp) {
        const { alarmTimestamp, source, value } = curAlarm;
        newestPerSource.set(source, { alarmTimestamp, source, value });
      }
    }
    try {
      for (const curAck of newestPerSource.values()) {
        if (!((yield* this.sendAlarmAck(curAck)) as boolean)) {
          yield put(raiseAction(ActionType.ACT_ACK_ALARM_FAIL, `Alarm ack failed.`));
          break;
        }
        yield put(raiseAction(ActionType.ACT_ACK_ALARM_SUCC, curAck));
      }
    } catch (error) {
      yield this.raiseHttpError(ActionType.ACT_ACK_ALARM_FAIL, error);
    }
    // load new state, also after a failure, as some acks may have succeeded
    yield put(raiseAction(ActionType.ACT_GET_ALARMS_REQ));
  }

  // disconnect the current site (if any) and select the new one
  protected *switchSite(action: IAction<number>): any {
    const state = (yield select(this.getState)) as IState;
    if (isSet(state.cached.selectedSite)) {
      yield put(raiseAction(ActionType.ACT_DISCONNECT_SITE));
    }
    yield put(raiseAction(ActionType.ACT_SELECT_SITE, action.payload));
  }

  public *raiseHttpError(actionFail: ActionType, error: unknown /* typically Response | Error */) {
    yield put(raiseAction(actionFail, errorToString(error)));

    // is this an http unauthorized error?
    // login attempts do not use this method, so any 401 is a session expiration
    if (error instanceof Response && error.status === 401) {
      yield put(raiseAction(ActionType.ACT_HTTP_ERR_UNAUTHORIZED));
      // let main scheduler task start immediately
      yield put(raiseAction(ActionType.ACT_AWAKE));
    }
  }

  public *checkForNewPeriod(curTimeOfPeriod: string) {
    const state = (yield select(this.getState)) as IState;
    const timeOfPeriod = extractArrayData(
      curTimeOfPeriod,
      DataType.INT,
      state.para.parameter?.tariffline.length,
    );
    let hasPeriodSwitch = false;
    state.data.tariffcircuit.forEach((curTariff, curIndex) => {
      if (curTariff.timeOfPeriod > timeOfPeriod[curIndex]) {
        hasPeriodSwitch = true;
      }
    });
    if (hasPeriodSwitch) {
      yield put(raiseAction(ActionType.ACT_NEW_PERIOD));
    }
  }
}
